import { useEffect, useMemo, useState } from 'react'
import { Search, UserPlus, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { Card } from '@/components/common/Card'
import { Input } from '@/components/common/Input'
import { Modal } from '@/components/common/Modal'
import { PageSpinner } from '@/components/common/Spinner'
import { useAuth } from '@/hooks/useAuth'
import UsersService from '@/services/users.service'
import type { User } from '@/types'

type UserRole = User['role']

const roleOptions: Array<{ value: UserRole | 'all'; label: string }> = [
  { value: 'all', label: 'Todas as roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'agent', label: 'Agente' },
  { value: 'operator', label: 'Operator' },
  { value: 'viewer', label: 'Viewer' },
]

const statusOptions = [
  { value: 'all', label: 'Todos os status' },
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Inativos' },
]

const roleVariant: Record<UserRole, 'brand' | 'info' | 'success' | 'default'> = {
  admin: 'brand',
  supervisor: 'info',
  agent: 'success',
  operator: 'info',
  viewer: 'default',
}

interface InviteFormState {
  full_name: string
  email: string
  username: string
  role: UserRole
}

const emptyInviteForm: InviteFormState = {
  full_name: '',
  email: '',
  username: '',
  role: 'agent',
}

export default function UsersPage() {
  const { user: currentUser, isAdmin, isSuperAdmin } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedRole, setSelectedRole] = useState<UserRole | 'all'>('all')
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'true' | 'false'>('all')
  const [inviteForm, setInviteForm] = useState<InviteFormState>(emptyInviteForm)

  const canManageUsers = isAdmin || isSuperAdmin
  const isReadOnlyManager = false

  async function loadUsers() {
    setIsLoading(true)
    try {
      const response = await UsersService.getAll({
        search: search || undefined,
        role: selectedRole === 'all' ? undefined : selectedRole,
        is_active:
          selectedStatus === 'all'
            ? undefined
            : selectedStatus === 'true',
        per_page: 100,
      })
      setUsers(response.data)
    } catch {
      toast.error('Não foi possível carregar os usuários.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadUsers().catch(() => undefined)
  }, [search, selectedRole, selectedStatus])

  const totals = useMemo(() => {
    const active = users.filter((item) => item.is_active).length
    const admins = users.filter((item) => item.role === 'admin').length
    return {
      total: users.length,
      active,
      inactive: users.length - active,
      admins,
    }
  }, [users])

  async function handleInviteSubmit() {
    if (!inviteForm.full_name.trim() || !inviteForm.email.trim()) {
      toast.error('Preencha nome e e-mail para enviar o convite.')
      return
    }

    setIsSaving(true)
    try {
      const created = await UsersService.invite({
        email: inviteForm.email.trim(),
        full_name: inviteForm.full_name.trim(),
        username: inviteForm.username.trim() || undefined,
        role: inviteForm.role,
      })
      toast.success(`Convite criado para ${created.full_name ?? created.email}.`)
      setInviteForm(emptyInviteForm)
      setIsInviteOpen(false)
      await loadUsers()
    } catch {
      toast.error('Não foi possível convidar o usuário.')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleToggleUser(user: User) {
    try {
      const updated = await UsersService.update(user.id, {
        is_active: !user.is_active,
      })
      setUsers((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      )
      toast.success(
        updated.is_active
          ? `${updated.full_name ?? updated.email} foi reativado.`
          : `${updated.full_name ?? updated.email} foi desativado.`,
      )
    } catch {
      toast.error('Não foi possível atualizar o status do usuário.')
    }
  }

  async function handleRoleChange(user: User, role: UserRole) {
    if (role === user.role) return
    try {
      const updated = await UsersService.updateRole(user.id, role)
      setUsers((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      )
      toast.success(`Role de ${updated.full_name ?? updated.email} atualizada para ${updated.role}.`)
    } catch {
      toast.error('Não foi possível atualizar a role do usuário.')
    }
  }

  if (isLoading && users.length === 0) {
    return <PageSpinner label="Carregando usuários..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-[var(--text-muted)]">
            Gestão de usuários, roles e ativação por workspace.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => loadUsers().catch(() => undefined)}>
            Atualizar
          </Button>
          {canManageUsers && (
            <Button icon={<UserPlus size={14} />} onClick={() => setIsInviteOpen(true)}>
              Convidar usuário
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-5">
          <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Total</p>
          <h3 className="mt-2 text-3xl font-semibold">{totals.total}</h3>
        </Card>
        <Card className="p-5">
          <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Ativos</p>
          <h3 className="mt-2 text-3xl font-semibold text-[var(--color-success)]">{totals.active}</h3>
        </Card>
        <Card className="p-5">
          <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Inativos</p>
          <h3 className="mt-2 text-3xl font-semibold text-[var(--color-error)]">{totals.inactive}</h3>
        </Card>
        <Card className="p-5">
          <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Admins</p>
          <h3 className="mt-2 text-3xl font-semibold text-[var(--color-brand)]">{totals.admins}</h3>
        </Card>
      </div>

      {isReadOnlyManager && (
        <Card className="p-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-[rgba(59,130,246,0.15)] p-2 text-[var(--color-info)]">
              <ShieldCheck size={16} />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-semibold">Modo supervisão</h3>
              <p className="text-sm text-[var(--text-muted)]">
                Supervisores podem acompanhar a base de usuários e aplicar filtros,
                mas convites, troca de role e ativação ficam restritos a administradores.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-5 space-y-4">
        <div className="grid gap-4 lg:grid-cols-[1.5fr,0.8fr,0.8fr]">
          <Input
            label="Buscar"
            placeholder="Nome, e-mail ou username"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            leftIcon={<Search size={16} />}
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[var(--text-primary)]">Role</label>
            <select
              value={selectedRole}
              onChange={(event) => setSelectedRole(event.target.value as UserRole | 'all')}
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[var(--text-primary)]">Status</label>
            <select
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value as 'all' | 'true' | 'false')}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <div className="grid gap-4">
        {users.map((user) => {
          const isSelf = currentUser?.id === user.id
          return (
            <Card key={user.id} className="p-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3>{user.full_name ?? 'Sem nome'}</h3>
                    <Badge variant={roleVariant[user.role]}>
                      {user.role}
                    </Badge>
                    <Badge variant={user.is_active ? 'success' : 'error'}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                    {isSelf && <Badge variant="brand">Você</Badge>}
                  </div>
                  <p>{user.email}</p>
                  <p className="text-sm text-[var(--text-muted)]">
                    Username: {user.username ?? 'não definido'}
                  </p>
                  <p className="text-sm text-[var(--text-muted)]">
                    Último login: {user.last_login ? new Date(user.last_login).toLocaleString('pt-BR') : 'ainda não registrado'}
                  </p>
                </div>

                <div className="flex flex-col gap-3 lg:min-w-[260px]">
                  {canManageUsers ? (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-[var(--text-primary)]">Role</label>
                      <select
                        value={user.role}
                        onChange={(event) => handleRoleChange(user, event.target.value as UserRole)}
                      >
                        {roleOptions
                          .filter((option) => option.value !== 'all')
                          .map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                      <ShieldCheck size={14} />
                      Role gerenciada por administradores
                    </div>
                  )}

                  {canManageUsers && !isSelf && (
                    <Button
                      variant={user.is_active ? 'outline' : 'success'}
                      onClick={() => handleToggleUser(user)}
                    >
                      {user.is_active ? 'Desativar usuário' : 'Reativar usuário'}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )
        })}

        {users.length === 0 && !isLoading && (
          <Card className="p-8">
            <h2>Nenhum usuário encontrado</h2>
            <p>
              {canManageUsers
                ? 'Refine os filtros ou envie um novo convite para o workspace.'
                : 'Refine os filtros para localizar um usuário existente no workspace.'}
            </p>
          </Card>
        )}
      </div>

      <Modal
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        title="Convidar usuário"
        description="Cria o usuário no workspace com role inicial e senha temporária."
      >
        <div className="space-y-4">
          <Input
            label="Nome completo"
            value={inviteForm.full_name}
            onChange={(event) =>
              setInviteForm((current) => ({ ...current, full_name: event.target.value }))
            }
            placeholder="Nome da pessoa"
          />
          <Input
            label="E-mail"
            type="email"
            value={inviteForm.email}
            onChange={(event) =>
              setInviteForm((current) => ({ ...current, email: event.target.value }))
            }
            placeholder="email@empresa.com"
          />
          <Input
            label="Username opcional"
            value={inviteForm.username}
            onChange={(event) =>
              setInviteForm((current) => ({ ...current, username: event.target.value }))
            }
            placeholder="usuario"
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[var(--text-primary)]">Role inicial</label>
            <select
              value={inviteForm.role}
              onChange={(event) =>
                setInviteForm((current) => ({ ...current, role: event.target.value as UserRole }))
              }
            >
              {roleOptions
                .filter((option) => option.value !== 'all')
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setIsInviteOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleInviteSubmit} loading={isSaving}>
              Enviar convite
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
